import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import type { PropertyAnalysisResult } from '../types/calculations';
import { useActiveMarket } from '../store/useMarketStore';

interface UnderwritingOptions {
    mode: 'off-market' | 'on-market';
    hold_period_years: number;
    purchase_price?: number;  // Required for on-market mode
    financing_type?: 'conventional' | 'seller' | 'cash';
    market_id?: string;  // Active market identifier (default: 'philadelphia')
}

interface UnderwriteResponse {
    property_id: number;
    mode: string;
    suggested_offer: number | null;
    purchase_price: number | null;
    full_analysis: PropertyAnalysisResult | null;
}

export const usePropertyUnderwriting = (propertyId: string, options: UnderwritingOptions = { mode: 'off-market', hold_period_years: 10 }) => {
    const activeMarket = useActiveMarket();
    return useQuery({
        queryKey: ['underwriting', propertyId, options, activeMarket.market_id, 'v39.0'],
        queryFn: async () => {
            const payload = { ...options, market_id: activeMarket.market_id };
            const { data } = await api.post<UnderwriteResponse>(
                `/v2/properties/${propertyId}/underwrite`,
                payload
            );
            // Backend returns UnderwriteResponse with full_analysis nested inside
            // Extract and return the PropertyAnalysisResult
            if (data.full_analysis) {
                return data.full_analysis;
            }
            // Fallback: maybe the API returned PropertyAnalysisResult directly
            return data as unknown as PropertyAnalysisResult;
        },
        enabled: !!propertyId,
        staleTime: 30 * 60 * 1000, // 30 min — backend caches for 1hr
    });
};
